import { createContext, useCallback, useMemo } from 'react';
import qs from 'qs';
import { navigate } from 'gatsby';
import { type WindowLocation } from '@reach/router';

const DEFAULT_HITSPERPAGE = 12;

const indexes: { [key: string]: string } = {
  Blog: 'blog_articles',
};

const placeholders: { [key: string]: string } = {
  Blog: 'Search Blog',
};

export type SearchConfigurePropsType = {
  hitsPerPage: number;
  restrictSearchableAttributes?: string[];
};

interface SearchContextProps {
  index: string;
  query: string;
  isSearching: boolean;
  hasSearchIndex: boolean;
  configureProps: SearchConfigurePropsType;
  type: string;
  searchInputDefaultValue: string;
  searchInputPlaceholder: string;
  performSearch: (value: string) => void;
  clearSearch: () => void;
}

export const SearchContext = createContext({
  index: 'blog_articles',
  query: '',
  isSearching: false,
  hasSearchIndex: false,
  configureProps: {
    hitsPerPage: DEFAULT_HITSPERPAGE,
    restrictSearchableAttributes: undefined,
  },
  type: 'query',
  searchInputDefaultValue: '',
  searchInputPlaceholder: '',
  performSearch: () => {},
  clearSearch: () => {},
} as SearchContextProps);

interface SearchContextProviderProps {
  category: string;
  type?: 'query' | 'tag';
  location: WindowLocation;
  children: JSX.Element;
  initialQuery?: string;
}

export const SearchContextProvider = ({
  initialQuery,
  type = 'query',
  category,
  location,
  children,
}: SearchContextProviderProps) => {
  const query =
    initialQuery ?? (qs.parse(location.search, { ignoreQueryPrefix: true }).query as string);

  const index = indexes[category];
  const isSearching = query !== undefined && query !== '';
  const pathname = location.pathname;
  const searchInputDefaultValue = type === 'tag' ? '' : query;
  const searchInputPlaceholder = placeholders[category];

  const configureTypes = {
    query: {
      hitsPerPage: DEFAULT_HITSPERPAGE,
    },
    tag: {
      hitsPerPage: DEFAULT_HITSPERPAGE,
      restrictSearchableAttributes: ['tags'],
    },
  };

  const configureProps = configureTypes[type];

  const clearSearch = useCallback(() => {
    return navigate('/blog/');
  }, []);

  const performSearch = useCallback(
    (value: string) => {
      const searchPath = type === 'query' ? pathname : '/blog/';
      const searchQuery = value === '' ? '' : `?query=${value}`;
      return navigate(`${searchPath}${searchQuery}`);
    },
    [type, pathname]
  );

  const contextValue = useMemo(
    () => ({
      index,
      hasSearchIndex: index !== undefined,
      query,
      isSearching,
      configureProps,
      type,
      searchInputDefaultValue,
      searchInputPlaceholder,
      clearSearch,
      performSearch,
    }),
    [
      index,
      query,
      isSearching,
      configureProps,
      type,
      searchInputDefaultValue,
      searchInputPlaceholder,
      clearSearch,
      performSearch,
    ]
  );

  return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>;
};

export default SearchContext;
